import { Injectable, inject } from '@angular/core';
import { classes } from 'polytype';

import type {
    DeploymentDto,
    DeploymentFigureDto,
    DeploymentInvocationLogsDto,
    DeploymentInvocationStatsDto,
} from '@app/management/deployment/deployment.dto';
import { DEPLOYMENT_PATH } from '@app/shared/api/api-route.config';
import {
    type ItemResponse,
    type ListResponse,
    type StatusResponse,
    WithCrudService,
} from '@app/shared/api/crud.service';
import { RestService } from '@app/shared/api/rest.service';
import { WithLogService } from '@app/shared/change-log/log.service';
import { ToastNotificationService } from '@app/shared/notification/toast-notification.service';

@Injectable({
    providedIn: 'root',
})
export class DeploymentService extends classes(WithLogService, WithCrudService<DeploymentDto>) {
    protected endpoint = DEPLOYMENT_PATH;

    protected readonly api = inject(RestService);
    protected readonly toast = inject(ToastNotificationService);

    getUiUrl(id: number) {
        return `${this.api.baseUrl()}${this.endpoint}${id}/ui`;
    }

    getLogs(id: number, startDate: string | null, endDate: string | null) {
        return this.api.getWithParams<ItemResponse<DeploymentInvocationLogsDto>>(
            `${this.endpoint}${id}/invocation_logs`,
            {
                startDate,
                endDate,
            },
        );
    }

    getStats(id: number, startDate: string | null, endDate: string | null) {
        return this.api.getWithParams<ItemResponse<DeploymentInvocationStatsDto>>(
            `${this.endpoint}${id}/stats`,
            {
                startDate,
                endDate,
            },
        );
    }

    getStatsFigures(deploymentId: number, startDate: string | null, endDate: string | null) {
        return this.api.getWithParams<{ result: DeploymentFigureDto[] }>(
            `${this.endpoint}${deploymentId}/stats/figures`,
            {
                startDate,
                endDate,
            },
        );
    }

    saveStatsFigure(deploymentId: number, figure: Partial<DeploymentFigureDto>) {
        return this.api.post<ItemResponse<DeploymentFigureDto>>(
            `${this.endpoint}${deploymentId}/stats/figure`,
            figure,
        );
    }

    reorderStatsFigures(deploymentId: number, figureIds: number[]) {
        return this.api.post<ListResponse<DeploymentFigureDto>>(
            `${this.endpoint}${deploymentId}/stats/figures/reorder`,
            {
                figureIds,
            },
        );
    }

    runStatsFigure(
        deploymentId: number,
        figureId: number,
        startDate: string | null,
        endDate: string | null,
    ) {
        return this.api.getWithParams<ItemResponse<DeploymentFigureDto>>(
            `${this.endpoint}${deploymentId}/stats/figure/${figureId}`,
            {
                startDate,
                endDate,
            },
        );
    }

    deleteStatsFigure(deploymentId: number, figureId: number) {
        return this.api.delete<StatusResponse>(
            `${this.endpoint}${deploymentId}/stats/figure/${figureId}`,
        );
    }
}
